import { bind, TimeoutHelper, raceTimeout } from '@asamco/utils';
import { MonoTypeOperatorFunction, Observable, Subject, combineLatest } from 'rxjs';
import { filter, first, map, scan, shareReplay, mergeMap } from 'rxjs/operators';
import { CspSignalrBase } from './csp-signalr-base';

export abstract class AckReadySignalrBase extends CspSignalrBase {
	protected payloadReadySubject: Subject<any> = new Subject<any>();
	protected acknowledgeRequestSubject: Subject<any> = new Subject<any>();
	protected connectionResultSubject: Subject<any> = new Subject<any>();

	// make sure to add to, not overwrite these...
	protected events = [
		{ methodName: 'PayloadReady', newMethod: this.payloadReady },
		{ methodName: 'AcknowledgeRequest', newMethod: this.acknowledgeRequest },
		{ methodName: 'ConnectionResult', newMethod: this.connectionResult },
	];

	@bind
	payloadReady(payloadReadyResult) {
		this.payloadReadySubject.next(payloadReadyResult);
	}

	@bind
	acknowledgeRequest(acknowledgeResult) {
		this.acknowledgeRequestSubject.next(acknowledgeResult);
	}

	@bind
	connectionResult(connectionResult) {
		this.connectionResultSubject.next(connectionResult);
	}

	public payloadReadyObservable(): Observable<any> {
		return this.payloadReadySubject.asObservable();
	}

	private payloadPool$ = this.payloadReadyObservable().pipe(
		scan((acc, ack) => {
			acc.set(ack.requestId, ack);
			return acc;
		}, new Map<string, any>()),
		shareReplay(1)
	);

	public acknowledgeRequestObservable(): Observable<any> {
		return this.acknowledgeRequestSubject.asObservable();
	}

	private ackPool$ = this.acknowledgeRequestObservable().pipe(
		scan((acc, ack) => {
			acc.set(ack.requestId, ack);
			return acc;
		}, new Map<string, any>()),
		shareReplay(1)
	);

	public connectionResultObservable(): Observable<any> {
		return this.connectionResultSubject.asObservable();
	}

	addFunctionAck(timeout = TimeoutHelper.AckTimeout) {
		return (obs$: Observable<any>) => combineLatest([obs$, this.ackPool$])
		.pipe(
			filter(([rm, ackMap]) => {
				const hasIt = ackMap.has(rm.requestId);
				return hasIt;
			}),
			raceTimeout(timeout, TimeoutHelper.AckFailMessage),
			map(([rm]) => rm)
		);
	}

	addAckAndPayload(): MonoTypeOperatorFunction<any> {
		return obs$ => {
			const acked = obs$.pipe(
				this.addFunctionAck()
			);

			return combineLatest([acked, this.payloadPool$]).pipe(
				filter(([rm, payloadMap]) => {
					const hasIt = payloadMap.has(rm.requestId);
					return hasIt;
				}),
				map(([rm, payloadMap]) => {
					const r = payloadMap.get(rm.requestId);
					if (r.status === 'Critical') {
						let message = r.message || '';
						if (!message.endsWith('.')) message += '.';
						throw new Error(`${message}`);
					} else if (r.status === 'ErrorMessage') {
						throw new Error(r.message);
					}
					return r;
				}),
				first(r => r.status === 'Success')
			);
		};
	}
}
